import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  IconButton,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemText,
  Button,
  Divider,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import NotificationsNoneIcon from "@material-ui/icons/NotificationsNone";
import api from "../../services/api";
import { getSocket } from "../../services/socket-io";
import { AuthContext } from "../../context/Auth/AuthContext";
import { fmtDateTimeShort } from "../../translate/localeHelper";

const useStyles = makeStyles((theme) => ({
  popoverBox: { width: 400, maxHeight: 500, overflow: "auto" },
  header: {
    padding: theme.spacing(1.5),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  unreadItem: {
    backgroundColor: theme.palette.type === "dark" ? "#3a2f1a" : "#fff3e0",
  },
}));

const fmtDateSafe = (d) => {
  try {
    return d ? fmtDateTimeShort(d) : "";
  } catch {
    return "";
  }
};

const NotificationBell = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);

  // super superadmin = superadmin sin scope de marca (feat_236)
  const isSuperSuperadmin = !!user?.isSuperAdmin && !user?.whitelabelScopeId;

  const fetchNotifs = useCallback(async () => {
    try {
      const { data } = await api.get("/superadmin/company-creation-notifications");
      setNotifs(data.notifications || []);
      setUnread(data.unread || 0);
    } catch (err) {
      /* silent */
    }
  }, []);

  useEffect(() => {
    if (!isSuperSuperadmin) return undefined;
    fetchNotifs();

    const socket = getSocket();
    const handler = () => fetchNotifs();
    socket?.on("company-creation-notification", handler);
    return () => {
      socket?.off("company-creation-notification", handler);
    };
  }, [isSuperSuperadmin, fetchNotifs]);

  if (!isSuperSuperadmin) return null;

  const handleMarkAllRead = async () => {
    try {
      await api.post("/superadmin/company-creation-notifications/mark-all-read");
      fetchNotifs();
    } catch (err) {
      /* silent */
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await api.post(`/superadmin/company-creation-notifications/${id}/mark-read`);
      fetchNotifs();
    } catch (err) {
      /* silent */
    }
  };

  return (
    <>
      <IconButton color="inherit" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <Badge badgeContent={unread} color="secondary">
          <NotificationsNoneIcon />
        </Badge>
      </IconButton>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <div className={classes.popoverBox}>
          <div className={classes.header}>
            <strong>Notificaciones ({unread} sin leer)</strong>
            {unread > 0 && (
              <Button size="small" onClick={handleMarkAllRead}>
                Marcar todo leído
              </Button>
            )}
          </div>
          <Divider />
          <List dense>
            {notifs.length === 0 ? (
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant="body2" color="textSecondary">
                      Sin notificaciones
                    </Typography>
                  }
                />
              </ListItem>
            ) : (
              notifs.map((n) => (
                <ListItem
                  key={n.id}
                  button
                  onClick={() => n.readAt || handleMarkRead(n.id)}
                  className={n.readAt ? undefined : classes.unreadItem}
                >
                  <ListItemText
                    primary={`🏢 ${n.company?.name || "—"} (${n.planAtCreation || "—"})`}
                    secondary={`${n.createdBy?.name || "—"} · ${
                      n.whitelabelScope?.name || "—"
                    } · ${fmtDateSafe(n.createdAt)}`}
                  />
                </ListItem>
              ))
            )}
          </List>
        </div>
      </Popover>
    </>
  );
};

export default NotificationBell;
